import { ApiError } from '@lib/network/api';
import {
  getSocialPlatformModule,
  type SocialAccountCapabilities,
} from '@lib/social/scheduling/platforms';
import { userStore } from '@stores/User';
import { observable, reaction, runInAction } from 'mobx';

export type SocialCapabilitiesState = {
  fetching: boolean;
  /** Set when the read failed: the form then offers the platform's defaults and says so. */
  error: Error | null;
  capabilities: SocialAccountCapabilities | null;
};

/** The account the capabilities are asked for: its id keys the cache, its platform picks the module that reads the answer. */
export type SocialCapabilitiesSubject = { id: string; platform: string };

/** `GET /social/connections/{id}/capabilities` as the API sends it, before the platform's module reads the `available: true` arm. */
type SocialAccountCapabilitiesWire = { available?: boolean; code?: string };

const toCapabilities = (
  wire: SocialAccountCapabilitiesWire,
  platform: string,
): SocialAccountCapabilities => {
  if (!wire.available) return { available: false, code: wire.code || 'platform_unavailable' };
  const module = getSocialPlatformModule(platform);
  // The form asks for an account it can compose to, which is one with a module.
  if (!module) throw new Error(`capabilities asked for unsupported platform ${platform}`);
  return module.parseCapabilities(wire);
};

/**
 * What each connected account may post right now, keyed by connection id.
 * Read live from the platform and rate-limited (15 a minute per user), so an
 * account is asked once per session, when the compose form first selects it,
 * and never on a keystroke. A failed read is retried on the next `load`.
 * Not a `DataFrame`: one value per account, asked for one account at a time.
 */
export class SocialCapabilitiesStore {
  private readonly byConnection = observable.map<string, SocialCapabilitiesState>();
  private crossStoreReactionsBound = false;

  /** Bound from `initCrossStoreReactions()`: the cache is one user's, so it goes when the user does. */
  initCrossStoreReactions() {
    if (this.crossStoreReactionsBound) return;
    this.crossStoreReactionsBound = true;
    reaction(
      () => userStore.data?._id,
      () => this.byConnection.clear(),
    );
  }

  state(connectionId: string): SocialCapabilitiesState | undefined {
    return this.byConnection.get(connectionId);
  }

  /** The account's capabilities once read; null before that and after a failed read. */
  capabilities(connectionId: string): SocialAccountCapabilities | null {
    return this.byConnection.get(connectionId)?.capabilities ?? null;
  }

  /** Ask once: a held or in-flight account is not asked again. */
  async load({ id, platform }: SocialCapabilitiesSubject): Promise<void> {
    const current = this.byConnection.get(id);
    if (current?.fetching || current?.capabilities) return;
    runInAction(() => {
      this.byConnection.set(id, { fetching: true, error: null, capabilities: null });
    });
    try {
      const wire = await userStore.apiWithAuth<SocialAccountCapabilitiesWire>(
        `/social/connections/${id}/capabilities`,
      );
      const capabilities = toCapabilities(wire, platform);
      runInAction(() => {
        this.byConnection.set(id, { fetching: false, error: null, capabilities });
      });
    } catch (error) {
      // 409 needs_reauth, 503 unreachable and 429 are the account's or the platform's state, worded by the
      // form; anything else (an answer in another platform's shape included) is a broken endpoint and has to reach the console.
      const expected =
        error instanceof ApiError &&
        (error.code === 'social.connection.needs_reauth' ||
          error.status === 503 ||
          error.status === 429);
      if (!expected) console.error('Failed to read account capabilities', error);
      runInAction(() => {
        this.byConnection.set(id, {
          fetching: false,
          error: error instanceof Error ? error : new Error(String(error)),
          capabilities: null,
        });
      });
    }
  }
}

export const socialCapabilitiesStore = new SocialCapabilitiesStore();
